import type {
  Bundle,
  Faq,
  Feature,
  FeaturedTitle,
  ProductDetail,
  ProductShot,
  Review,
  StoryPanel,
  TrustItem,
} from '../types/blackout'

const REAL_FRONT =
  'https://cdn.magicpatterns.com/uploads/ixvSzA9vgFfVZNTHmoz2hu/hf_20260505_160349_8ac9e7f0-517f-4f74-9ee5-49caa262d662.png'
const REAL_SIDE =
  'https://cdn.magicpatterns.com/uploads/wktPfPj4sasfKV5cbuidEQ/hf_20260505_160726_c7d8c9e1-4986-4b8e-9be1-eab15717a38a.png'
const REAL_MODEL =
  'https://cdn.magicpatterns.com/uploads/styiL95jqae3dHQjsU8SNs/hf_20260519_153516_d713b8fb-1d1a-416c-9a61-f78daa6411f2_(1).png'

export const images = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/f407d5ff-d3cf-47d2-8297-440fd099f60e.jpg',
  productFront: REAL_FRONT,
  crewFlag:
    'https://cdn.magicpatterns.com/patterns/generated-images/4dc274a8-e417-4e75-a60d-987111a741bc.jpg',
  finalCity:
    'https://cdn.magicpatterns.com/patterns/generated-images/7b9f690f-7c17-425b-b550-82017aee6684.jpg',
}

export const trustItems: TrustItem[] = [
  { icon: 'shipping', title: 'Free U.S. shipping', detail: 'On bundles of 3+' },
  { icon: 'secure', title: 'Secure checkout', detail: 'Shop Pay · PayPal · cards' },
  { icon: 'quality', title: 'Dark matte finish', detail: 'Sculpted skull build' },
  { icon: 'crew', title: 'Built for real life', detail: 'Events · crews · content' },
]

export const platformNames = [
  'Netflix',
  'HBO Max',
  'Amazon Prime',
  'Activision',
  'Warner Bros',
  'Lionsgate',
]

// Reality → story → reality. Real photos validate, ink panels create the feeling.
export const storyPanels: StoryPanel[] = [
  {
    id: 'hood',
    chapter: 'After dark',
    world: 'reality',
    caption: 'It starts with a hood.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/27880604-eeeb-47e1-91e8-19f3c8448de3.jpg',
    alt: 'Hooded figure in the skull mask on a foggy street at night',
  },
  {
    id: 'mask-on',
    chapter: 'After dark',
    world: 'story',
    caption: 'Put it on. Something changes.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ff2500b6-b0c6-43c1-86c4-5a7ca35db7ae.jpg',
    alt: 'Ink close-up of a hooded character in the skull mask',
  },
  {
    id: 'crew-real',
    chapter: 'The crew',
    world: 'reality',
    caption: 'Bigger than one.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b3d5d952-bf92-4c4e-9ec2-c65e8b77f502.jpg',
    alt: 'Masked crew walking through an underground garage',
  },
  {
    id: 'crew-story',
    chapter: 'The crew',
    world: 'story',
    caption: 'One night a year. No rules. No faces.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2689105a-8b06-465f-853a-7bf09deba332.jpg',
    alt: 'Ink panel of masked figures on an empty street at night',
  },
  {
    id: 'ride',
    chapter: 'The ride',
    world: 'story',
    caption: 'Anywhere. Anonymous. Together.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fc029469-bc2f-483b-b855-3899b7ff9352.jpg',
    alt: 'Ink panel of a masked rider on a motorcycle in front of the city',
  },
  {
    id: 'entrance',
    chapter: 'The entrance',
    world: 'story',
    caption: 'Every entrance needs a character.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c717ad95-5cc5-47d7-b6df-92f82f90d8ab.jpg',
    alt: 'Ink panel of a masked character walking into an arena under spotlights',
  },
  {
    id: 'city',
    chapter: 'The city',
    world: 'reality',
    caption: 'Same streets. Different perspective.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/097bdb59-8eac-4d04-8450-08e2a586143b.jpg',
    alt: 'Lone hooded figure on a rooftop above the city at night',
  },
  {
    id: 'outside',
    chapter: 'The outside',
    world: 'story',
    caption: 'It fits real life.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1ef6d084-6737-45c4-b8e4-ee57c0ef2bd1.jpg',
    alt: 'Ink panel of a hooded masked figure in a mountain forest',
  },
  {
    id: 'return',
    chapter: 'Back to reality',
    world: 'reality',
    caption: 'Back in the real world. Still different.',
    image: REAL_MODEL,
    alt: 'Real photo of a model wearing the BU-01 Ultimate Mask',
  },
]

export const productShots: ProductShot[] = [
  { id: 'front', label: 'Front', image: REAL_FRONT, alt: 'BU-01 Ultimate Mask, front view' },
  { id: 'side', label: 'Side', image: REAL_SIDE, alt: 'BU-01 Ultimate Mask, side profile' },
  { id: 'model', label: 'On model', image: REAL_MODEL, alt: 'Model wearing the BU-01 Ultimate Mask' },
]

export const productDetails: ProductDetail[] = [
  { id: 'eyes', label: 'Twin eye crystals', image: REAL_FRONT, zoom: 2.3, origin: '50% 38%' },
  { id: 'teeth', label: 'Sculpted teeth', image: REAL_FRONT, zoom: 2.1, origin: '50% 74%' },
  { id: 'vents', label: 'Vented face', image: REAL_FRONT, zoom: 2.6, origin: '30% 64%' },
  { id: 'strap', label: 'Strap system', image: REAL_SIDE, zoom: 1.9, origin: '78% 38%' },
]

export const features: Feature[] = [
  { icon: 'skull', title: 'Sculpted skull profile', description: 'Angular forehead, sculpted teeth, side detail.' },
  { icon: 'eyes', title: 'Twin smoke eye crystals', description: 'Two separate smoke-black crystals, installed.' },
  { icon: 'vents', title: 'Vented face design', description: 'Cheek ventilation keeps air moving.' },
  { icon: 'strap', title: 'Adjustable strap system', description: 'Dials in to fit most adults.' },
  { icon: 'finish', title: 'Dark matte finish', description: 'Swallows light. Reads from across the room.' },
]

/**
 * Multi-mask deals add the single-mask variant at quantity N. The deal price
 * is applied at checkout by a matching Shopify automatic discount, and free
 * shipping by a matching Shopify shipping rule — both live in Shopify admin.
 * Ids stay unchanged so pixel event contents stay consistent.
 */
const SINGLE_VARIANT = 44483157852253

export const bundles: Bundle[] = [
  {
    id: 'origin',
    number: '01',
    name: 'Origin Issue',
    deal: '1 mask',
    tagline: 'Standard smoke-black eye crystals included.',
    cta: 'Begin your story',
    count: 1,
    price: 29.95,
    regularPrice: 29.95,
    popular: false,
    shipping: 'applies',
    cartUrl: `https://blackoutunit.com/cart/${SINGLE_VARIANT}:1`,
  },
  {
    id: 'triple',
    number: '02',
    name: 'Triple Arc',
    deal: 'Buy 2, get 1 free',
    tagline: 'Bring two. Nobody goes alone.',
    cta: 'Build your unit',
    count: 3,
    price: 59.95,
    regularPrice: 89.85,
    badge: 'Recommended',
    popular: true,
    shipping: 'free',
    bonus: 'Alternate color crystal set',
    cartUrl: `https://blackoutunit.com/cart/${SINGLE_VARIANT}:3`,
    // Buy Now links per number of Triple Arc packs. Add 2, 3, 4 when ready.
    quantityLinks: {
      1: 'https://blackoutunit.com/cart/45086596563037:1',
    },
  },
  {
    id: 'crew',
    number: '03',
    name: 'Crew Edition',
    deal: 'Buy 3, get 2 free',
    tagline: 'Friends, riders, events.',
    cta: 'Build the crew',
    count: 5,
    price: 89.95,
    regularPrice: 149.75,
    badge: 'For crews',
    popular: false,
    shipping: 'free',
    bonus: '2 alternate color crystal sets',
    cartUrl: `https://blackoutunit.com/cart/${SINGLE_VARIANT}:5`,
    // Buy Now links per number of Crew Edition packs. Add 2, 3, 4 when ready.
    quantityLinks: {
      1: 'https://blackoutunit.com/cart/45086634704989:1',
    },
  },
  {
    id: 'collector',
    number: '04',
    name: 'Blackout Eight',
    deal: 'Buy 4, get 4 free',
    tagline: 'The whole unit. Lowest price per mask.',
    cta: 'Build the unit',
    count: 8,
    price: 119.95,
    regularPrice: 239.6,
    badge: 'Best value',
    popular: false,
    shipping: 'free',
    bonus: '4 alternate color crystal sets',
    cartUrl: `https://blackoutunit.com/cart/${SINGLE_VARIANT}:8`,
  },
]

export const featuredBundle = bundles.find((b) => b.popular) ?? bundles[0]

// Content carried over unchanged from the "As Seen In" section.
export const featuredTitles: FeaturedTitle[] = [
  {
    image:
      'https://cdn.magicpatterns.com/uploads/5taRZeiHKbeHUgT9mvbj8f/hf_20260519_141137_57b95531-a597-4707-a3ba-98dbd149b0ec.png',
    title: 'Call of Duty',
    subtitle: 'Simon "Ghost" Riley',
    type: 'Video game · Action',
    platform: 'Activision',
  },
  {
    image:
      'https://cdn.magicpatterns.com/uploads/3snzvUnWRWmgNfKZ25UMLW/hf_20260519_141536_43f5116e-4a0d-41b9-adcf-ee03778920ab.png',
    title: 'The Town',
    subtitle: 'Ben Affleck · Jeremy Renner',
    type: 'Crime · Thriller',
    platform: 'HBO Max',
  },
  {
    image:
      'https://cdn.magicpatterns.com/uploads/6aBwpUozKskfcjRf6H5Pjr/hf_20260519_142247_77b42ac5-d266-4fdb-aba9-4088926a7447.png',
    title: 'Marauders',
    subtitle: 'Bruce Willis · Dave Bautista',
    type: 'Action · Crime',
    platform: 'Amazon Prime',
  },
  {
    image:
      'https://cdn.magicpatterns.com/uploads/nLLHhr5CSE1oEBGbKrjhSY/hf_20260519_142513_f490f85f-18eb-4985-9094-d1e29583a797.png',
    title: 'Field Ops',
    subtitle: 'Real Tactical Deployment',
    type: 'Operator · Classified',
    platform: 'Classified',
  },
]

/**
 * PLACEHOLDER REVIEWS — design-phase content only.
 * Names, quotes and photos are NOT real customers. Replace every entry with
 * real reviews (and real photos) from the store's review app before launch,
 * and set `verified: true` only for confirmed orders. The rating summary on
 * the page is computed from this list, so it becomes real once this is real.
 */
export const reviews: Review[] = [
  {
    id: 'placeholder-1',
    name: 'Marcus T.',
    location: 'Austin, TX',
    rating: 5,
    topic: 'Quality',
    quote: 'Looks way better in person. Wore it to a night event and got non-stop compliments.',
    purchase: 'Triple Arc',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/76822a96-8dc4-48f8-9ab2-6cf8eb6f18ac.jpg',
    imageAlt: 'Customer wearing the mask at a party',
    verified: false,
  },
  {
    id: 'placeholder-2',
    name: 'Jalen M.',
    location: 'Atlanta, GA',
    rating: 5,
    topic: 'Crew order',
    quote: 'Got the crew pack. We wear these to events and shoots. Total game changer.',
    purchase: 'Crew Edition',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/974b8a37-f274-428d-977e-73430f43d246.jpg',
    imageAlt: 'Three friends wearing matching masks',
    verified: false,
  },
  {
    id: 'placeholder-3',
    name: 'Ryan K.',
    location: 'Phoenix, AZ',
    rating: 5,
    topic: 'Fit',
    quote: 'Comfortable and the strap locks in. The dark eye crystals make it look so clean.',
    purchase: 'Origin Issue',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/36cb729e-ad77-4590-afaf-af14e23a9b61.jpg',
    imageAlt: 'Customer mirror selfie wearing the mask',
    verified: false,
  },
  {
    id: 'placeholder-4',
    name: 'Dante V.',
    location: 'Las Vegas, NV',
    rating: 5,
    topic: 'Rides',
    quote: 'Wear it on night rides with the guys. People stop us at every gas station.',
    purchase: 'Crew Edition',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d9d40234-0e35-44a9-9651-7a143539f2a1.jpg',
    imageAlt: 'Customer on a motorcycle wearing the mask',
    verified: false,
  },
  {
    id: 'placeholder-5',
    name: 'Chris D.',
    location: 'Tampa, FL',
    rating: 5,
    topic: 'Build',
    quote: 'Solid build, real vents, and it just looks mean. Exactly what I wanted.',
    purchase: 'Triple Arc',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/69b605a9-c72c-481b-8d24-8ffba6126517.jpg',
    imageAlt: 'Close-up of the mask a customer received',
    verified: false,
  },
  {
    id: 'placeholder-6',
    name: 'Jake R.',
    location: 'Columbus, OH',
    rating: 5,
    topic: 'Compliments',
    quote: 'Walked into the party and the whole room turned. Feels like a different version of me.',
    purchase: 'Triple Arc',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7afefbc7-1477-43fe-b5ed-39861c052a9a.jpg',
    imageAlt: 'Customer wearing the mask at a Halloween party',
    verified: false,
  },
  {
    id: 'placeholder-7',
    name: 'Alex R.',
    location: 'Denver, CO',
    rating: 5,
    topic: 'Realism',
    quote: 'Higher quality than I expected. You can tell this isn’t a cheap costume mask.',
    purchase: 'Origin Issue',
    image: REAL_MODEL,
    imageAlt: 'Customer wearing the BU-01 mask',
    verified: false,
  },
  {
    id: 'placeholder-8',
    name: 'Tony W.',
    location: 'San Antonio, TX',
    rating: 4,
    topic: 'Value',
    quote: 'Got the eight for the whole group. Great value per mask. Shipping took a few extra days.',
    purchase: 'Blackout Eight',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b3d5d952-bf92-4c4e-9ec2-c65e8b77f502.jpg',
    imageAlt: 'A group wearing matching masks',
    verified: false,
  },
]

// Answers follow the store's current Shipping and Returns policies.
export const faqs: Faq[] = [
  {
    question: 'What comes with each mask?',
    answer:
      'Every BU-01 comes with two smoke-black eye crystals installed and an adjustable strap. Some bundles also include bonus alternate color crystal sets — each offer lists exactly what’s included.',
  },
  {
    question: 'Are the eye crystals included?',
    answer:
      'Yes. Two separate smoke-black eye crystals come installed in every mask. They look dark from the outside while you see out from the inside.',
  },
  {
    question: 'How does the mask fit?',
    answer:
      'The adjustable strap system fits most adult head sizes. Tighten it once and it stays put through the night.',
  },
  {
    question: 'Do I need a full costume?',
    answer:
      'No. A black hoodie and the mask are enough to change the whole look. That’s the point.',
  },
  {
    question: 'Why buy the bundle?',
    answer:
      'Each step up costs $30 more and adds more masks, a lower price per mask, and free U.S. shipping (single-mask orders pay shipping). The free masks are applied automatically at checkout.',
  },
  {
    question: 'How fast does it ship?',
    answer:
      'Orders are processed within 24 hours after payment is verified (next business day on weekends and holidays) and ship via USPS. Delivery typically takes 3–5 business days after shipping. Timelines are estimates, not guarantees.',
  },
  {
    question: 'What is the return policy?',
    answer:
      'All sales are final. We review a replacement or return only if the wrong item was shipped, the product has a verified manufacturing defect, or it arrived damaged due to a fulfillment error. Claims must be sent within 48 hours of delivery to support@blackoutunit.com with your order number and photos.',
  },
]
